import React, { useState, useEffect, useMemo } from 'react';
import { Calculator, TrendingUp, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';

const fmt = (v) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', maximumFractionDigits: 0
}).format(Number.isFinite(v) ? v : 0);

const pct = (v) => `${Number.isFinite(v) ? v.toFixed(2) : '0.00'}%`;

function monthlyMortgage(principal, annualRatePct, years) {
  if (principal <= 0) return 0;
  if (annualRatePct <= 0) return principal / (years * 12);
  const r = annualRatePct / 100 / 12;
  const n = years * 12;
  return (principal * (r * Math.pow(1 + r, n))) / (Math.pow(1 + r, n) - 1);
}

export default function RentalCalculator({ askingPrice = 0, initialRent = 0 }) {
  const [rent, setRent] = useState(initialRent || Math.round((askingPrice || 0) * 0.01));
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(7.5);
  const [years, setYears] = useState(30);
  const [advOpen, setAdvOpen] = useState(false);
  const [maintPct, setMaintPct] = useState(5);
  const [vacPct, setVacPct] = useState(5);
  const [mgmtPct, setMgmtPct] = useState(8);
  const [closingPct, setClosingPct] = useState(3);
  const [taxRate, setTaxRate] = useState(1.2); // % of price/year
  const [insRate, setInsRate] = useState(0.5); // % of price/year
  const [hoa, setHoa] = useState(0);

  useEffect(() => {
    if (initialRent) setRent(initialRent);
    else if (askingPrice && !rent) setRent(Math.round(askingPrice * 0.01));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askingPrice, initialRent]);

  const calc = useMemo(() => {
    const price = Number(askingPrice) || 0;
    const monthlyRent = Number(rent) || 0;
    const down = price * (Number(downPct) / 100);
    const loan = price - down;
    const closing = price * (Number(closingPct) / 100);
    const cashInvested = down + closing;
    const mMortgage = monthlyMortgage(loan, Number(rate), Number(years));
    const mTax = (price * (Number(taxRate) / 100)) / 12;
    const mIns = (price * (Number(insRate) / 100)) / 12;
    const mMaint = monthlyRent * (Number(maintPct) / 100);
    const mVac = monthlyRent * (Number(vacPct) / 100);
    const mMgmt = monthlyRent * (Number(mgmtPct) / 100);
    const totalExp = mMortgage + mTax + mIns + Number(hoa) + mMaint + mVac + mMgmt;
    const cashflow = monthlyRent - totalExp;
    const annualCashflow = cashflow * 12;
    const annualNOI = monthlyRent * 12 - (mTax + mIns + Number(hoa) + mMaint + mVac + mMgmt) * 12;
    const capRate = price > 0 ? (annualNOI / price) * 100 : 0;
    const coc = cashInvested > 0 ? (annualCashflow / cashInvested) * 100 : 0;
    return { down, loan, closing, cashInvested, mMortgage, mTax, mIns, mMaint, mVac, mMgmt, totalExp, cashflow, annualCashflow, capRate, coc };
  }, [askingPrice, rent, downPct, rate, years, maintPct, vacPct, mgmtPct, closingPct, taxRate, insRate, hoa]);

  const positive = calc.cashflow >= 0;

  return (
    <div className="bg-white p-6 rounded-xl border border-slate-200" data-testid="deal-calculator">
      <div className="flex items-center gap-2 mb-2">
        <Calculator className="h-5 w-5 text-blue-700" />
        <h2 className="font-heading text-xl font-semibold text-slate-900">Rental Cashflow Calculator</h2>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Real numbers, not vibes. Adjust the inputs to model your deal.
      </p>

      {/* Primary inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="text-xs text-slate-600">Monthly Rent</Label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="number"
              value={rent}
              onChange={(e) => setRent(e.target.value)}
              className="pl-9"
              data-testid="calc-monthly-rent"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-600">Down Payment %</Label>
          <Input
            type="number"
            value={downPct}
            onChange={(e) => setDownPct(e.target.value)}
            data-testid="calc-down-pct"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Interest Rate %</Label>
          <Input
            type="number"
            step="0.1"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            data-testid="calc-rate"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Loan Term (years)</Label>
          <Input
            type="number"
            value={years}
            onChange={(e) => setYears(e.target.value)}
            data-testid="calc-years"
          />
        </div>
      </div>

      {/* Advanced */}
      <button
        type="button"
        onClick={() => setAdvOpen((v) => !v)}
        className="flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-800 mb-4"
        data-testid="calc-toggle-advanced"
      >
        {advOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        Advanced assumptions
      </button>

      {advOpen && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 p-4 bg-slate-50 rounded-lg" data-testid="calc-advanced">
          <div>
            <Label className="text-xs text-slate-600">Taxes % / yr</Label>
            <Input type="number" step="0.1" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Insurance % / yr</Label>
            <Input type="number" step="0.1" value={insRate} onChange={(e) => setInsRate(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">HOA / mo</Label>
            <Input type="number" value={hoa} onChange={(e) => setHoa(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Closing %</Label>
            <Input type="number" step="0.1" value={closingPct} onChange={(e) => setClosingPct(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Maintenance %</Label>
            <Input type="number" step="0.5" value={maintPct} onChange={(e) => setMaintPct(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Vacancy %</Label>
            <Input type="number" step="0.5" value={vacPct} onChange={(e) => setVacPct(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Mgmt %</Label>
            <Input type="number" step="0.5" value={mgmtPct} onChange={(e) => setMgmtPct(e.target.value)} />
          </div>
        </div>
      )}

      {/* Results */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className={`p-4 rounded-lg ${positive ? 'bg-green-50' : 'bg-red-50'}`} data-testid="calc-cashflow">
          <div className={`text-xs font-medium ${positive ? 'text-green-600' : 'text-red-600'}`}>Monthly Cashflow</div>
          <div className={`text-xl font-bold ${positive ? 'text-green-700' : 'text-red-700'}`}>
            {fmt(calc.cashflow)}
          </div>
        </div>
        <div className="p-4 bg-blue-50 rounded-lg" data-testid="calc-coc">
          <div className="text-xs text-blue-600 font-medium flex items-center gap-1">
            <TrendingUp className="h-3.5 w-3.5" /> Cash-on-Cash
          </div>
          <div className="text-xl font-bold text-blue-700">{pct(calc.coc)}</div>
        </div>
        <div className="p-4 bg-slate-100 rounded-lg" data-testid="calc-caprate">
          <div className="text-xs text-slate-600 font-medium">Cap Rate</div>
          <div className="text-xl font-bold text-slate-900">{pct(calc.capRate)}</div>
        </div>
        <div className="p-4 bg-amber-50 rounded-lg" data-testid="calc-cash-invested">
          <div className="text-xs text-amber-600 font-medium">Cash to Close</div>
          <div className="text-xl font-bold text-amber-700">{fmt(calc.cashInvested)}</div>
        </div>
      </div>

      {/* Breakdown */}
      <details className="text-sm">
        <summary className="cursor-pointer text-slate-500 hover:text-slate-700 select-none">
          See monthly expense breakdown
        </summary>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 p-4 bg-slate-50 rounded-lg">
          <span className="text-slate-600">Mortgage (P&I)</span><span className="text-right font-medium">{fmt(calc.mMortgage)}</span>
          <span className="text-slate-600">Taxes</span><span className="text-right font-medium">{fmt(calc.mTax)}</span>
          <span className="text-slate-600">Insurance</span><span className="text-right font-medium">{fmt(calc.mIns)}</span>
          <span className="text-slate-600">HOA</span><span className="text-right font-medium">{fmt(Number(hoa))}</span>
          <span className="text-slate-600">Maintenance</span><span className="text-right font-medium">{fmt(calc.mMaint)}</span>
          <span className="text-slate-600">Vacancy reserve</span><span className="text-right font-medium">{fmt(calc.mVac)}</span>
          <span className="text-slate-600">Property management</span><span className="text-right font-medium">{fmt(calc.mMgmt)}</span>
          <span className="text-slate-900 font-semibold border-t border-slate-200 pt-2">Total expenses</span>
          <span className="text-right font-semibold text-slate-900 border-t border-slate-200 pt-2">{fmt(calc.totalExp)}</span>
        </div>
      </details>

      <p className="text-xs text-slate-400 mt-4">
        Estimates only. Verify all numbers with your lender, insurer & local rental comps before making an offer.
      </p>
    </div>
  );
}
